
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

const EnergyStoragePage = () => {
  useEffect(() => {
    // SEO設定
    document.title = '蓄電所運営事業 | 株式会社伊藤総業 - エネルギー貯蔵システム';
    
    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "蓄電所運営事業 - 株式会社伊藤総業",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/energy-storage`,
      "description": "株式会社伊藤総業の蓄電所運営事業。大規模蓄電システムの企画・開発・設計から運営まで、エネルギー貯蔵の総合ソリューションを提供。",
      "mainEntity": {
        "@type": "Service",
        "name": "蓄電所運営事業",
        "provider": {
          "@type": "Organization",
          "name": "株式会社伊藤総業"
        },
        "serviceType": "蓄電所運営",
        "areaServed": {
          "@type": "Place",
          "name": "東京都世田谷区"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <main>
        <section 
          className="relative h-96 bg-cover bg-center flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/5ae7ccb43e0196fb647b329013292c0e.jpg')`
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-white max-w-2xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">蓄電所運営事業</h1>
              <p className="text-xl mb-6">
                最新技術による効率的なエネルギー貯蔵システムの企画・開発・設計・運営
              </p>
              <Link 
                to="/contact" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">蓄電所運営サービス</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                再生可能エネルギーの安定供給を実現する蓄電所の企画から運営まで、トータルソリューションを提供いたします。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-lightbulb-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">企画・設計</h3>
                <p className="text-gray-600">
                  立地条件や需要予測を基に、最適な蓄電所の企画・設計を行います。効率性と安全性を両立したシステム設計をご提案いたします。
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">開発・建設</h3>
                <p className="text-gray-600">
                  最新の蓄電技術を活用し、高性能な蓄電所の開発・建設を実施。品質管理を徹底し、長期間安定稼働する施設を構築します。
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-settings-3-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">運営・管理</h3>
                <p className="text-gray-600">
                  24時間365日の監視体制により、蓄電所の安全で効率的な運営を実現。定期メンテナンスと最適化により長期安定稼働を保証します。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Features */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">最新蓄電技術の活用</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                      <i className="ri-battery-charge-line text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">高効率リチウムイオン電池</h3>
                      <p className="text-gray-600">最新のリチウムイオン電池技術により、高いエネルギー密度と長寿命を実現しています。</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-1">
                      <i className="ri-cpu-line text-green-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">AI制御システム</h3>
                      <p className="text-gray-600">人工知能による需要予測と最適制御により、効率的なエネルギー管理を実現します。</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4 mt-1">
                      <i className="ri-shield-check-line text-orange-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">安全管理システム</h3>
                      <p className="text-gray-600">多重安全装置と24時間監視により、安全で信頼性の高い運営を保証します。</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Advanced%20battery%20energy%20storage%20system%20with%20monitoring%20screens%20and%20control%20panels%2C%20modern%20industrial%20facility%20interior%2C%20blue%20LED%20lighting%2C%20high-tech%20equipment%20with%20digital%20displays%20showing%20energy%20flow%20data&width=600&height=400&seq=energy-storage-tech&orientation=landscape"
                  alt="蓄電所技術システム"
                  className="rounded-lg shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">蓄電所運営のメリット</h2>
              <p className="text-lg text-gray-600">
                持続可能なエネルギー社会の実現に向けた価値を提供します
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-leaf-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold text-lg mb-2">環境負荷軽減</h3>
                <p className="text-gray-600 text-sm">再生可能エネルギーの有効活用により、CO2削減に貢献</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold text-lg mb-2">コスト削減</h3>
                <p className="text-gray-600 text-sm">ピークカットと電力料金の最適化により運営コストを削減</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-flashlight-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold text-lg mb-2">安定供給</h3>
                <p className="text-gray-600 text-sm">電力需給バランスの調整により安定したエネルギー供給を実現</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold text-lg mb-2">収益性向上</h3>
                <p className="text-gray-600 text-sm">電力売買と調整力提供により安定した収益を創出</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">蓄電所開発の流れ</h2>
              <p className="text-lg text-gray-600">
                企画から運営開始まで、段階的にプロジェクトを進めます
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  1
                </div>
                <h3 className="font-semibold text-lg mb-2">調査・企画</h3>
                <p className="text-gray-600 text-sm">立地調査、需要分析、事業性評価を実施し、最適な蓄電所を企画します。</p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  2
                </div>
                <h3 className="font-semibold text-lg mb-2">設計・許認可</h3>
                <p className="text-gray-600 text-sm">詳細設計を行い、必要な許認可手続きを代行いたします。</p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  3
                </div>
                <h3 className="font-semibold text-lg mb-2">建設・試運転</h3>
                <p className="text-gray-600 text-sm">高品質な施工と徹底した試運転により、安全な蓄電所を構築します。</p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  4
                </div>
                <h3 className="font-semibold text-lg mb-2">運営開始</h3>
                <p className="text-gray-600 text-sm">24時間監視体制のもと、安全で効率的な運営を開始します。</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">蓄電所運営についてご相談ください</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              持続可能なエネルギー社会の実現に向けて、最適な蓄電所ソリューションをご提案いたします。
            </p>
            <Link 
              to="/contact" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              無料相談を申し込む
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EnergyStoragePage;
